import { useMemo } from "react";
import { Card, CardGroup, OverlayTrigger, Tooltip } from "react-bootstrap";

import { formatDineroCad } from "@/utils/dinero-util";
import { CostTaxBreakdownErrorsPopover } from "../job-tracking/components/CostTaxBreakdownErrorsPopover";
import { needToPurchase } from "../job-tracking/components/purchasing-progress/purchasing-progress-data";
import { withJobTrackingData } from "../job-tracking/components/withJobTrackingData";
import { BomComponent } from "../job-tracking/queries/tracking-data/bom-by-component";
import { CostTaxBreakdown } from "../job-tracking/queries/tracking-data/cost-tax-breakdown";
import { PriorityOption, priorityOptionColors, priorityOptions } from "./purchasing-priority-utils";
import { PurchasingPriorityBadge } from "./PurchasingPriorityBadge";

/**
 * Renders a component showing a summary of the purchasing costs associated with each purchasing priority/stage
 */
export const PurchasingPriorityCostSummary: React.FC = withJobTrackingData(({ bomComponents }) => {
  const costsByPriority = useMemo(() => calculateCostsByPriority(bomComponents), [bomComponents]);

  return (
    <CardGroup className="text-nowrap">
      {priorityOptions.map((priority) => (
        <PurchasingPriorityCostCard
          key={priority}
          priority={priority}
          cost={costsByPriority[priority]!.cost}
          numComponents={costsByPriority[priority]!.numComponents}
        />
      ))}
    </CardGroup>
  );
});

const PurchasingPriorityCostCard: React.FC<{
  priority: PriorityOption;
  cost: CostTaxBreakdown;
  numComponents: number;
}> = ({ priority, cost, numComponents }) => {
  return (
    <Card
      key={priority}
      className="col-auto text-dark"
      style={{ backgroundColor: priorityOptionColors[priority], flexBasis: "auto" }}
    >
      <Card.Body className="py-0 px-2">
        {cost.errors.length ?
          <CostTaxBreakdownErrorsPopover cost={cost} title={"Failed To Calculate Priority Costs"} />
        : <OverlayTrigger
            overlay={(props) => (
              <Tooltip {...props} style={{ ...props.style, "--bs-tooltip-max-width": "400px" }}>
                Total purchasing cost for <b>{numComponents}</b> items at priority{" "}
                <PurchasingPriorityBadge priority={priority} />:
                <br />
                {formatDineroCad(cost.subtotal)} + {formatDineroCad(cost.tax)} tax = {formatDineroCad(cost.total)}
                <br />
                <b>NOTE:</b> This estimate does not include any shipping costs
              </Tooltip>
            )}
          >
            <Card.Title className="d-inline">{formatDineroCad(cost.total)}</Card.Title>
          </OverlayTrigger>
        }
      </Card.Body>
    </Card>
  );
};

function calculateCostsByPriority(bomComponents: BomComponent[]) {
  // Filter the BOM components to only those that need to be purchased
  const componentsToPurchase = bomComponents.filter((component) => needToPurchase(component));

  // Get a list of all components that will be purchased at each priority level
  const componentsByPriority = priorityOptions.map((priority) =>
    componentsToPurchase.filter((component) => component.quantitiesByPriority.get(priority)!.nonInventoryQuantity),
  );

  // Calculate the total costs for each priority option
  // This is a cumulative sum (i.e. stage 2 includes all the costs for stage 1)
  const totalCostsByPriority = priorityOptions.map((priority, index) =>
    componentsByPriority[index]!.reduce((total, component) => {
      const componentQuantity = component.quantitiesByPriority.get(priority)!.nonInventoryQuantity;
      const componentCost = component.unitCost.multiply(componentQuantity);
      return total.add(componentCost);
    }, CostTaxBreakdown.ZERO),
  );

  // Calculate the total, non-cumulative, number of components and cost for each priority
  return Object.fromEntries(
    priorityOptions.map((priority, index) => {
      const numComponents =
        index === 0 ?
          componentsByPriority[index]!.length
        : componentsByPriority[index]!.length - componentsByPriority[index - 1]!.length;
      const totalCost = totalCostsByPriority[index]!;
      const cost = index === 0 ? totalCost : totalCost.subtract(totalCostsByPriority[index - 1]!);
      return [priority, { numComponents, cost }];
    }),
  );
}
